import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type HeaderConfigDTO,
  type HeaderOrderUpsertDTO,
  type TimelineLayoutDTO,
  upsertTimelineLayoutDTOSchema,
} from '@tempo-io/roadmap-dto';
import { toast } from '@tempo-io/sui-sonner';
import type { z } from 'zod';
import { TimelineLayoutApi } from '@/features/visualizations/api/timelineLayout';
import { visualizationQueryKeys } from '@/features/visualizations/queryKeys';

export const useTimelineHeaderVisibilityMutation = (viewId: number, visualizationId: number) => {
  const queryClient = useQueryClient();

  const hideHeaderMutation = useMutation({
    mutationFn: async ({
      headerFieldId,
      headerId,
      parentFieldHeaderId = null,
      parentHeaderId = null,
    }: {
      headerFieldId: string;
      headerId: string;
      parentFieldHeaderId?: string | null;
      parentHeaderId?: string | null;
    }) => {
      await TimelineLayoutApi.hideHeader(
        viewId,
        visualizationId,
        headerFieldId,
        headerId,
        parentFieldHeaderId,
        parentHeaderId,
      );
      return { headerFieldId, headerId, parentFieldHeaderId, parentHeaderId };
    },
    onSuccess: ({ headerFieldId, headerId, parentFieldHeaderId, parentHeaderId }) => {
      queryClient.invalidateQueries({
        queryKey: visualizationQueryKeys.timelineLayout.detail(viewId, visualizationId),
        refetchType: 'none',
      });
      queryClient.setQueriesData(
        {
          queryKey: visualizationQueryKeys.timelineLayout.headerConfig({
            viewId,
            visualizationId,
            headerFieldId,
            parentFieldId: parentFieldHeaderId,
            parentHeaderId,
          }),
        },
        (prev: HeaderConfigDTO | undefined) => {
          if (!prev) return prev;
          return {
            ...prev,
            hiddenHeaderIds: (prev.hiddenHeaderIds || []).includes(headerId)
              ? prev.hiddenHeaderIds
              : [...(prev.hiddenHeaderIds || []), headerId],
          };
        },
      );
    },
  });

  const showHeaderMutation = useMutation({
    mutationFn: async ({
      headerFieldId,
      headerId,
      parentFieldHeaderId = null,
      parentHeaderId = null,
    }: {
      headerFieldId: string;
      headerId: string;
      parentFieldHeaderId?: string | null;
      parentHeaderId?: string | null;
    }) => {
      await TimelineLayoutApi.showHeader(
        viewId,
        visualizationId,
        headerFieldId,
        headerId,
        parentFieldHeaderId,
        parentHeaderId,
      );
      return { headerFieldId, headerId, parentFieldHeaderId, parentHeaderId };
    },
    onSuccess: ({ headerFieldId, headerId, parentFieldHeaderId, parentHeaderId }) => {
      queryClient.invalidateQueries({
        queryKey: visualizationQueryKeys.timelineLayout.detail(viewId, visualizationId),
        refetchType: 'none',
      });
      queryClient.setQueriesData(
        {
          queryKey: visualizationQueryKeys.timelineLayout.headerConfig({
            viewId,
            visualizationId,
            headerFieldId,
            parentFieldId: parentFieldHeaderId,
            parentHeaderId,
          }),
        },
        (prev: HeaderConfigDTO | undefined) => {
          if (!prev) return prev;
          return {
            ...prev,
            hiddenHeaderIds: (prev.hiddenHeaderIds || []).filter((id) => id !== headerId),
          };
        },
      );
    },
  });

  return { hideHeaderMutation, showHeaderMutation };
};

type TimelineLayoutUpsertDTO = z.infer<typeof upsertTimelineLayoutDTOSchema>;

export const useUpsertTimelineLayoutMutation = (viewId: number, visualizationId: number) => {
  const queryClient = useQueryClient();
  const queryKey = visualizationQueryKeys.timelineLayout.detail(viewId, visualizationId);

  return useMutation({
    mutationFn: async (partialLayout: Partial<TimelineLayoutUpsertDTO>) => {
      const existingLayout = queryClient.getQueryData<TimelineLayoutDTO>(queryKey);
      const mergedLayout = { ...existingLayout, ...partialLayout };
      const parsedLayout = upsertTimelineLayoutDTOSchema.parse(mergedLayout);
      return TimelineLayoutApi.upsertTimelineLayout(viewId, visualizationId, parsedLayout);
    },
    onMutate: (updatedLayout) => {
      const previousLayout = queryClient.getQueryData<TimelineLayoutDTO>(queryKey);

      // TODO: figure out what should be done when previousLayout is not found
      if (!previousLayout) {
        throw new Error('Previous layout not found in query cache');
      }

      const newLayout = { ...previousLayout, ...updatedLayout };
      queryClient.setQueryData(queryKey, newLayout);

      return { previousLayout };
    },
    onError: (err, _updatedLayout, context) => {
      toast.error(err.message);
      if (context?.previousLayout) {
        queryClient.setQueryData(queryKey, context.previousLayout);
      }
    },
    onSuccess: (savedLayout) => {
      queryClient.setQueryData<TimelineLayoutDTO>(queryKey, (previousLayout) =>
        previousLayout ? { ...previousLayout, ...savedLayout } : savedLayout,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
    },
  });
};

export const useUpdateTimelineHeaderOrderMutation = (viewId: number, visualizationId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      headerFieldId,
      headerOrder,
    }: {
      headerFieldId: string;
      headerOrder: HeaderOrderUpsertDTO;
    }) => {
      const updatedHeaderOrder = await TimelineLayoutApi.updateHeaderOrder(
        viewId,
        visualizationId,
        headerFieldId,
        headerOrder,
      );
      return { updatedHeaderOrder, headerFieldId, headerOrder };
    },
    onError: (err) => {
      toast.error(err.message);
    },
    onSuccess: ({ updatedHeaderOrder, headerFieldId, headerOrder }) => {
      queryClient.invalidateQueries({
        queryKey: visualizationQueryKeys.timelineLayout.detail(viewId, visualizationId),
        refetchType: 'none',
      });
      queryClient.setQueriesData(
        {
          queryKey: visualizationQueryKeys.timelineLayout.headerConfig({
            viewId,
            visualizationId,
            headerFieldId,
            parentFieldId: headerOrder.parentFieldHeaderId,
            parentHeaderId: headerOrder.parentHeaderId,
          }),
        },
        (prev: HeaderConfigDTO | undefined) => {
          if (!prev) return prev;
          return {
            ...prev,
            orderedHeaderIds: updatedHeaderOrder.orderedHeaderIds,
          };
        },
      );
    },
  });
};
